import { post as rpcPost } from '@/helpers/api'
import { post, del } from '@/api/rest'
import store, {
	getMessage,
	setMessageAndMoveCursor,
	updateMessage,
	getClientMeta,
} from '@/store'
import { denormalizeMessage, setMedia } from '@/store/messages'
import { deleteUploadsForMessageUUID, setUpload } from '@/store/uploads'
import { showAlertModal } from '@/components/common'
import {
	normalizeImageForFileKey,
	transcodeVideoForFileKey,
	uploadMedia,
} from './upload'
import {
	mediaTypes,
	transcodeJobStatus,
	MAX_MESSAGE_MEDIA,
	MAX_MESSAGE_MEDIA_SIZE_BYTES,
} from '@/helpers/constants'
import {
	sleep,
	storage,
	fileSize,
	copyNativeFiles,
	normalizeNativeFile,
	normalizeMediaItem,
	getAsyncNativeFileImageDimensions,
	generateVideoThumbnailFromFile,
} from '@/helpers/utils'
import router from '@/router'
import { redirectToRecentsOrChat } from '@/helperMethods'
import { createLogger } from '@/helpers/log'
import { getPusherInstance } from '@/helpers/pusher'
import i18n from '@/i18n'
import { updateClientMeta } from '@/api/user'
import { addRecentEmoji } from '@/helpers/emojis'

export const fakeErrors = {
	SEND: 'throw_message_send_exception',
	RESPONSE: 'throw_message_response_exception',
	UPLOAD: 'throw_media_upload_exception',
	IMAGE: 'throw_image_normalization_exception',
	VIDEO: 'throw_video_transcoding_exception',
}

const sendingLog = createLogger('SENDING', true)
const uploadLog = createLogger('UPLOADS', true)

const sendIfAllUploadsComplete = async (media) => {
	// To allow background uploads/sends, when a file upload finishes, check the following:
	//   1. Check to see if the upload's message is in 'waiting' status
	//   2. If so, then check to see if all other media on the message have also completed
	// Then go ahead and send the message
	const message = getMessage(media.messageUUID)
	sendingLog(
		`upload "${media.title}" complete for message ${media.messageUUID}`
	)
	if (!message?.isWaiting) {
		sendingLog(`message is not waiting: "${message?.sendState}" so bailing`)
		return
	}
	const allCompleted = message.messagemedia.every((m) => m.isComplete)
	if (!allCompleted) {
		sendingLog(
			`message ${message.uuid} still has pending uploads so waiting longer`
		)
		return
	}
	sendingLog(`message ${message.uuid} ready to send, starting now...`)
	try {
		await sendMessage(message)
	} catch (e) {
		const isChatRoute = router.currentRoute.value.name === 'chat'
		const isSameChat = store.currentChatId === message.bubble_id
		if (!isChatRoute || !isSameChat) {
			sendingLog(`showing failed alert for ${message.uuid}`)
			const close = showAlertModal({
				// TODO: add these strings to Lokalise
				title: 'Failed to Send',
				description: i18n.global.t('model_message_senderror_notification'),
				confirmText: 'Open Chat',
				danger: true,
				onConfirm: () => {
					sendingLog(`closing failed alert for ${message.uuid}`)
					close()
					redirectToRecentsOrChat(message.bubble_id)
				},
			})
		}
	}
}

export const sendMessage = async (payload, cancelToken) => {
	try {
		sendingLog('starting message send...', payload)
		// check to see if any of the uploads failed and need to be retried
		const failedMedia = payload.messagemedia?.filter((m) => m.isFailed)
		if (failedMedia?.length) {
			sendingLog(
				`found ${failedMedia.length} failed uploads`,
				failedMedia.map((m) => m.tempId)
			)
			failedMedia.forEach((media) => {
				sendingLog('re-uploading failed media', media, payload.uuid)
				uploadMediaWithStore(media)
			})
			return
		}
		// tempId values get lost after response from the server, so we save them and
		// we'll replace them back onto the message media before it goes into the store
		const tempIdsByUUID = payload.messagemedia?.reduce((acc, m) => {
			acc[m.uuid] = m.tempId
			return acc
		}, {})
		// just to make sure we don't
		const { isSending, isSent, sendState } = getMessage(payload.uuid)
		if (isSending || isSent) {
			sendingLog(
				`bailing since message is not waiting to be sent: "${sendState}"`
			)
			return null
		}
		updateMessage(payload.uuid, { sendState: 'sending' })
		payload = denormalizeMessage(payload)

		if (storage.get(fakeErrors.SEND)) {
			// this is purely for testing/debugging purposes
			await sleep(1000)
			throw new Error(storage.get(fakeErrors.SEND))
		}

		const { data } = await rpcPost(
			'/api/v1/message.create',
			payload,
			cancelToken
		)
		if (!data.ok && data.error) {
			throw new Error(data.error)
		}

		if (storage.get(fakeErrors.RESPONSE)) {
			// this is purely for testing/debugging purposes
			throw new Error(storage.get(fakeErrors.RESPONSE))
		}
		const { message } = data
		message.sendState = 'sent'
		if (message.messagemedia?.length) {
			// restore message media tempId values
			message.messagemedia.forEach((media) => {
				if (tempIdsByUUID[media.uuid]) {
					media.tempId = tempIdsByUUID[media.uuid]
				}
			})
		}
		setMessageAndMoveCursor(message)
		setTimeout(() => {
			deleteUploadsForMessageUUID(message.uuid)
		}, 3000)

		if (message.parentmessage_id) {
			// preemptively update parent message
			const parent = getMessage(message.parentmessage_id)
			if (parent) {
				updateMessage(parent.id, {
					firstchildmessage_id: parent.firstchildmessage_id || message.id,
					lastchildmessage_id: message.id,
				})
			}
		}
		sendingLog('message sent and stored successfully', message.id, message.uuid)
		return message
	} catch (e) {
		console.error(e)
		sendingLog(`send failed for ${payload.uuid}`)
		updateMessage(payload.uuid, { sendState: 'failed' })
		throw e
	}
}

export const searchMessages = async (payload, cancelToken) => {
	try {
		const { data } = await rpcPost(
			'/api/v1/message.search?include_multidms=1',
			payload,
			cancelToken
		)
		return data
	} catch (e) {
		console.error(e)
		throw e
	}
}

export const uploadMediaWithStore = async (media) => {
	try {
		media.isFailed = false
		media.isComplete = false
		setUpload(media.tempId, {
			uploadProgress: 0,
			isFailed: false,
			isComplete: false,
		})
		if (storage.get(fakeErrors.UPLOAD)) {
			// this is purely for testing/debugging purposes
			for (let i = 1; i < 5; i++) {
				await sleep(300)
				setUpload(media.tempId, { uploadProgress: i * 15 })
			}
			throw new Error(storage.get(fakeErrors.UPLOAD))
		}
		// for images and non-media files, we upload directly via the api
		const { data: response } = await uploadMedia(media, {
			onProgress: (uploadProgress) => {
				// don't quite complete the progress until promise fully resolves
				if (uploadProgress < 100) {
					setUpload(media.tempId, { uploadProgress })
				}
			},
		})
		Object.assign(media, {
			size: fileSize(response.size),
			filesize: response.size,
			title: response.name,
			uuid: response.key,
			local_id: response.key,
			mimetype: response.type,
			urlmimetype: response.type,
			isComplete: !media.isVisual,
		})
		setMedia(media)
		uploadLog('upload complete', media.tempId, response)
		setUpload(media.tempId, { uuid: response.key, uploadProgress: 100 })

		if (media.isPhoto) {
			media = await normalizeImageMediaWithStore(media)
		} else if (media.isVideo) {
			media = await transcodeVideoMediaWithStore(media)
		}

		sendIfAllUploadsComplete(media)
	} catch (e) {
		console.error(e)
		uploadLog('upload failed', media.tempId)
		media.isFailed = true
		media.isComplete = true
		setMedia(media)
		setUpload(media.tempId, {
			uploadProgress: 0,
			isFailed: true,
			isComplete: true,
		})
	}

	return media
}

export const normalizeImageMediaWithStore = async (media) => {
	try {
		if (storage.get(fakeErrors.IMAGE)) {
			// this is purely for testing/debugging purposes
			await sleep(1000)
			throw new Error(storage.get(fakeErrors.IMAGE))
		}
		uploadLog('starting image normalization', media.tempId)
		const { normalized } = await normalizeImageForFileKey(media.uuid)
		uploadLog('normalization successful', media.tempId, normalized)
		const { key, mimetype, ...image } = normalized
		Object.assign(media, {
			...image,
			uuid: key,
			local_id: key,
			mimetype,
			urlmimetype: mimetype,
			isComplete: true,
		})
		setMedia(media)
		setUpload(media.tempId, {
			local_id: key,
			uuid: key,
			isNormalized: true,
		})
		return media
	} catch (e) {
		console.error(e)
		// in case the image fails to normalize, we treat the image as a FILE type
		uploadLog('normalizaton failed, changing to FILE', media.tempId)
		Object.assign(
			media,
			normalizeMediaItem({
				...media,
				type: mediaTypes.FILE,
				mediatype: mediaTypes.FILE,
				isComplete: true,
			})
		)
		setMedia(media)
		setUpload(media.tempId, { mediatype: mediaTypes.FILE, isComplete: true })
		return media
	}
}

export const transcodeVideoMediaWithStore = async (media) => {
	return new Promise(async (resolve) => {
		try {
			if (storage.get(fakeErrors.VIDEO)) {
				// this is purely for testing/debugging purposes
				await sleep(1000)
				throw new Error(storage.get(fakeErrors.VIDEO))
			}
			uploadLog('starting video transcoding', media.tempId)
			let job = await transcodeVideoForFileKey(media.uuid)
			uploadLog('transcode request successful', media.tempId, job)
			media = processTranscodeJob(media, job)

			const pusher = getPusherInstance()
			const channel = pusher.subscribe(job.pusher_channel)
			// binding to channel directly instead of pusher instance so that
			// we only get events specific to this job id, not all jobs
			channel.bind('App\\Events\\TranscodeUpdated', (job) => {
				uploadLog('transcoding updated event', job)
				media = processTranscodeJob(media, job)
				if (media.isComplete) {
					uploadLog('transcoding complete', job)
					channel.unbind()
					pusher.unsubscribe(job.pusher_channel)
					resolve(media)
				}
			})
			uploadLog('waiting for trascode updates from pusher', media.tempId, job)
		} catch (e) {
			console.error(e)
			// in case the video fails to transcode, we treat it as a FILE type
			uploadLog('transcoding failed, changing to FILE', media.tempId)
			Object.assign(
				media,
				normalizeMediaItem({
					...media,
					mediatype: mediaTypes.FILE,
					isComplete: true,
				})
			)
			setMedia(media)
			setUpload(media.tempId, { mediatype: mediaTypes.FILE })
			return resolve(media)
		}
	})
}

export const prepFilesForUpload = async (
	nativeFiles,
	currentCount = 0,
	messageUUID
) => {
	const normalizedFiles = []
	if (!nativeFiles?.length) return normalizedFiles

	let filtered = Array.from(nativeFiles).filter((f) => f && f.size > 0)

	if (!filtered.length) {
		uploadLog('unsupported file(s)', copyNativeFiles(nativeFiles))
		window.prontoAlert.error(i18n.global.t('chat_unsupported_file_upload'))
		return normalizedFiles
	}

	const remainingAllowed = MAX_MESSAGE_MEDIA - currentCount
	let hasMoreThanMaxCount = false

	if (remainingAllowed < filtered.length) {
		filtered = filtered.slice(0, remainingAllowed)
		hasMoreThanMaxCount = true
		showAlertModal({
			title: i18n.global.t('chat_attachment_max_limit_exceeded_title'),
			description: i18n.global.t(
				'chat_attachment_max_limit_exceeded_description',
				{
					Count: MAX_MESSAGE_MEDIA,
				}
			),
		})
	}

	if (!filtered.length) return normalizedFiles
	let hasFileOverMaxSize = false

	for (const file of filtered) {
		const normalized = normalizeNativeFile(file)
		if (normalized.filesize > MAX_MESSAGE_MEDIA_SIZE_BYTES) {
			hasFileOverMaxSize = true
			continue
		}
		try {
			if (normalized.isPhoto) {
				const dimensions = await getAsyncNativeFileImageDimensions(file)
				Object.assign(normalized, dimensions)
			} else if (normalized.isVideo) {
				const metadata = await generateVideoThumbnailFromFile(file)
				Object.assign(normalized, metadata)
			}
		} catch (e) {
			const { name, size, type } = file
			console.error('failed to read native file', { name, size, type })
			normalized.mediatype = mediaTypes.FILE
			Object.assign(normalized, normalizeMediaItem(normalized))
		}
		normalized.messageUUID = messageUUID
		// kick off the async upload but don't wait for it to complete
		uploadMediaWithStore(normalized)
		normalizedFiles.push(normalized)
	}

	if (hasFileOverMaxSize && !hasMoreThanMaxCount) {
		showAlertModal({
			title: i18n.global.t('chat_attachment_max_limit_exceeded_title'),
			description: i18n.global.t(
				'chat_attachment_size_limit_exceeded_description',
				{
					Size: fileSize(MAX_MESSAGE_MEDIA_SIZE_BYTES),
				}
			),
		})
	}

	return normalizedFiles
}

const processTranscodeJob = (media, job) => {
	const isFailed = job.status === transcodeJobStatus.ERRORED
	const isComplete = job.status === transcodeJobStatus.COMPLETED
	const isDone = isComplete || isFailed
	const uploadData = {
		jobId: job.id,
		mediaUrl: media.thumbnailUrl,
		transcodeStatus: job.status,
		transcodeProgress: isDone ? 100 : job.percentage_completed,
		thumbnailKey: job.frame_key,
		outputKey: job.output_key,
		isComplete,
		isFailed,
		isTranscoded: isComplete,
	}
	Object.assign(media, {
		isComplete: isDone,
		local_id: uploadData.outputKey,
		uuid: uploadData.outputKey,
		thumbnailuuid: uploadData.thumbnailKey,
	})
	setMedia(media)
	setUpload(media.tempId, uploadData)
	return media
}

export const addReaction = async (messageId, emoji) => {
	try {
		const { data } = await post(`/messages/${messageId}/reactions`, {
			emoji,
		})
		await addRecentEmoji(emoji)
		return data
	} catch (e) {
		console.error(e)
		throw e
	}
}

export const removeReaction = async (messageId, emoji) => {
	try {
		const { data } = await del(`/messages/${messageId}/reactions/${emoji}`)
		return data
	} catch (e) {
		console.error(e)
		throw e
	}
}
